import { useState } from 'react';
import { Link } from 'react-router';
import type { IconType } from 'react-icons';
// The `Lu` set, not `Fa`: these are drawn as strokes rather than filled
// silhouettes, which is what the site design uses — its own perk glyphs are
// `fill="none" stroke-width="1.8"`. A solid icon next to this type reads
// heavier than the text it labels.
import { LuCheck, LuRotateCw } from 'react-icons/lu';

import { cn } from '@mds/shared/lib/utils';

/**
 * A card's width in the package row the home page and a space's page both
 * draw. It is the width the cards had when the home page showed three, in a
 * 3fr/2fr split with the illustration: a third of the 3fr, less the two gaps
 * between cards. `100%` is the band, so it holds at every page width.
 */
export const PACKAGE_CARD_WIDTH =
  'calc(((100% - var(--spacing-card)) * 3 / 5 - 2 * var(--spacing-card)) / 3)';

/**
 * The width of the cards' own grid: `count` cards and the gaps between them.
 * The illustration sits beside that grid, not in it, and takes what is left.
 * Set on a direct child of the band so `100%` in the card width is the band.
 * An inline `width`, since it is a `calc` on a token.
 */
export function packageCardsWidth(count: number) {
  return `calc(${count} * ${PACKAGE_CARD_WIDTH} + ${Math.max(count - 1, 0)} * var(--spacing-card))`;
}

/**
 * One line on the back of a card.
 *
 * `icon` is the component itself, not a rendered element, so the card decides
 * its size and colour — those have to change with the face it lands on, and a
 * caller passing `<LuCar />` would have baked in the wrong ones.
 */
export type Perk = { label: string; icon?: IconType };

export type PackageCardProps = {
  /** "Daily Desk". */
  name: string;
  /** The figure alone — "$10". */
  price: string;
  /** What the figure buys — "/ day". Kept separate so it can be set quieter. */
  unit: string;
  /** The mono label on the back — "Flexible", "6-month term". */
  tag: string;
  /**
   * Optional bullets on the back — the package's benefits. Any number — the card grows to fit
   * the longest back in its row rather than cropping.
   *
   * A bare string gets a tick; pass `{ label, icon }` to name the thing the
   * line is about, which is what the design does — a clock for the meeting-room
   * hour, a car for parking, a cup for the café. Both forms can be mixed in one
   * list.
   */
  perks?: (string | Perk)[];
  /**
   * The back's call to action — "Book a day".
   *
   * Optional, and currently passed by nobody: the back reads as information
   * rather than as a thing to act on, and the row's action is the package you
   * pick, not a link per card. The rendering is kept rather than deleted so
   * turning it back on is passing the prop, not rewriting the component.
   */
  cta?: { label: string; to: string };
  /**
   * Marks this as the package to pick.
   *
   * It draws a mint ring and adds a "Most popular" label, and it changes no
   * fill at all: white front and black back, the same as every other package.
   *
   * The ring rather than an inverted fill because a fill can only mark a card
   * in one of its two states: invert this one and the mark vanishes the moment
   * the row flips and every card shows the other colour. A ring sits on both
   * faces and says the same thing in both.
   */
  featured?: boolean;
  className?: string;
};

/**
 * Shared by both faces: same box, same corner, same cell.
 *
 * `col-start-1 row-start-1` is what makes the two faces share one grid cell
 * rather than being taken out of flow with `absolute`. That is not a stylistic
 * preference — it is what gives the card a height. Absolutely positioned faces
 * contribute nothing to their parent's size, so the card needs a fixed height,
 * and a fixed height silently crops whichever face outgrows it: a back with
 * five perks would simply lose the last two. Stacked in a grid cell, the cell
 * is as tall as the taller face and nothing is ever cut off.
 */
const FACE =
  'relative col-start-1 row-start-1 flex flex-col overflow-hidden rounded-card shadow-card [backface-visibility:hidden]';

/**
 * A desk package, with the detail on the back.
 *
 * Flips on hover, on keyboard focus, and on a press of "View details" — the
 * last of those is not a nicety. A card that only opens on hover is a card that
 * never opens on a phone, and its back is content, not decoration. Pressing the
 * back turns it over again, which is the other half of the same point: on a
 * touch screen there is no pointer to take off the card, so without it the card
 * opens once and stays open.
 *
 * The hidden face is `inert`, so whichever side is turned away is out of the
 * tab order and out of the accessibility tree. Without it both faces are
 * announced at once and the card reads as gibberish — `backface-visibility`
 * hides pixels, not content.
 */
export default function PackageCard({
  name,
  price,
  unit,
  tag,
  perks,
  cta,
  featured,
  className,
}: PackageCardProps) {
  // Three states, not two: `null` is "follow the pointer and the focus", and a
  // press pins the card open or shut until the pointer leaves it. A plain
  // boolean cannot express the shut half — `pressed || pointerOver` means a
  // click on the back while the cursor is still on the card turns nothing
  // over, which is every click a mouse makes.
  const [pressed, setPressed] = useState<boolean | null>(null);
  const [pointerOver, setPointerOver] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);

  const showBack = pressed ?? (pointerOver || focusWithin);

  return (
    // `min-h`, not `h`: 250px is the design's height and the floor a sparse
    // card sits at, but a card with more on its back grows past it instead of
    // cropping. As a grid item this also stretches to the tallest card in its
    // row, so a row of five stays a row of five equal cards — the one with
    // five perks sets the height and the rest follow it.
    <div
      className={cn('min-h-62.5 perspective-[1400px]', className)}
      onMouseEnter={() => setPointerOver(true)}
      // Leaving the card drops the pin as well as the hover, so the next
      // approach starts from hover again rather than from whatever the last
      // press left behind.
      onMouseLeave={() => {
        setPointerOver(false);
        setPressed(null);
      }}
      onFocus={() => setFocusWithin(true)}
      onBlur={() => setFocusWithin(false)}
    >
      <div
        className={cn(
          'grid h-full transition-transform duration-(--duration-base)',
          'ease-standard transform-3d motion-reduce:transition-none',
        )}
        style={showBack ? { transform: 'rotateY(180deg)' } : undefined}
      >
        {/* Front */}
        <div
          inert={showBack}
          className={cn(
            FACE,
            'justify-center gap-1.5 bg-surface px-5 pt-6 pb-10 text-ink',
            // No border on an ordinary card: white on the page's off-white is
            // enough of an edge, and a hairline on every card would make the
            // featured one's ring read as "this card has a thicker border"
            // rather than as a mark. The ring means one thing here.
            featured && 'ring-2 ring-brand-edge',
          )}
        >
          {featured && (
            <span className="absolute top-5 left-5 font-mono text-2xs font-bold text-brand-text uppercase">
              Most popular
            </span>
          )}

          <h3 className="text-heading-md">{name}</h3>
          <span className="text-4xl">
            {price}
            <span className="ml-1 text-md font-medium text-ink-muted">{unit}</span>
          </span>

          <button
            type="button"
            onClick={() => setPressed(true)}
            // Announced as a toggle rather than a link, because it does not go
            // anywhere — it turns the card over.
            aria-expanded={showBack}
            className={cn(
              'group/flip absolute right-4 bottom-3 inline-flex items-center gap-1.5',
              'rounded-sm text-xs font-medium text-brand-text',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
            )}
          >
            View details
            {/* A rotate glyph, not an arrow. The arrow that was here promised
                to take you somewhere; this button turns the card over and stays
                on the page, and an icon that lies about where a control leads
                is worse than no icon. It turns a half-circle on hover, in the
                same direction and over the same beat as the card itself. */}
            <LuRotateCw
              aria-hidden="true"
              className={cn(
                'size-3 transition-transform duration-(--duration-base) ease-standard',
                'group-hover/flip:rotate-180 motion-reduce:transition-none',
              )}
            />
          </button>
        </div>

        {/* Back. Black for every package, the featured one included — so the
            ring is what marks it, and it is the only thing that does. The ring
            is on both faces, which is what keeps the mark from disappearing
            the moment the whole row turns over. */}
        <div
          inert={!showBack}
          data-ground="panel"
          className={cn(
            FACE,
            'transform-[rotateY(180deg)] gap-2 bg-panel p-4 text-on-panel',
            // The bright mint, not the mid green the front's ring uses: on
            // black, mint-700 is 4.5:1 and mint-500 is 14:1. Same mark, the
            // step that can be seen on each ground.
            featured && 'ring-2 ring-brand',
          )}
        >
          {/* The way back, and it is the whole face rather than a corner
              control: the back is the state you leave, so the target for
              leaving it should be as big as the card. A real button, not a
              click handler on the panel — this is the only way out on a touch
              screen, where there is no pointer to take off the card, and it
              has to be reachable by keyboard and announced as a toggle. It
              sits under the text so the layout above is untouched. */}
          <button
            type="button"
            onClick={() => setPressed(false)}
            aria-expanded={showBack}
            aria-label={`Hide ${name} details`}
            className={cn(
              'absolute inset-0 rounded-card',
              'focus-visible:outline-2 focus-visible:-outline-offset-2',
              'focus-visible:outline-focus-on-panel',
            )}
          />

          {/* `pointer-events-none` so the text does not shield the button
              behind it; the link is the one thing that takes its clicks back. */}
          <div className="pointer-events-none relative flex flex-1 flex-col gap-2">
            <span className="font-mono text-2xs text-brand uppercase">{tag}</span>

            {perks && (
              <ul className="flex list-none flex-col gap-1.5 text-2xs text-on-panel/75">
                {perks.map((perk) => {
                  const { label, icon: Icon = LuCheck } =
                    typeof perk === 'string' ? { label: perk, icon: undefined } : perk;

                  return (
                    <li key={label} className="flex items-start gap-2">
                      {/* `mt-0.5` rather than `items-center`: a perk can wrap
                          to two lines, and centring would then float the icon
                          down beside the middle of the sentence instead of
                          leaving it beside the first line where it is read. */}
                      <Icon className="mt-0.5 size-2.5 shrink-0 text-brand" aria-hidden="true" />
                      {label}
                    </li>
                  );
                })}
              </ul>
            )}

            {cta && (
              <Link
                to={cta.to}
                className={cn(
                  // `pt-3` on top of `mt-auto`: the auto margin pushes this to
                  // the bottom of a sparse card, but on the tallest card in
                  // the row there is no slack left and the link ends up
                  // sitting straight under the last perk. The padding is the
                  // gap that survives both.
                  'pointer-events-auto mt-auto self-start rounded-sm pt-3 text-xs font-bold',
                  // Mint, and the bright step of it — this sits on the black
                  // back, where the mid green the front uses is 4.5:1 and this
                  // is 14:1. No underline: the colour and the weight carry it,
                  // and the nudge on hover is what confirms it is live.
                  'text-brand',
                  'transition-transform duration-(--duration-fast) ease-standard',
                  'hover:translate-x-0.5',
                  'focus-visible:outline-2 focus-visible:outline-offset-2',
                  'focus-visible:outline-focus-on-panel',
                )}
              >
                {cta.label}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
